import { useState, useRef } from "react";
import { DataTable } from "../components/DataTable";
import { useAuth } from "../context/AuthContext";

type User = {
  id: string;
  fullName: string;
  email: string;
  mobileNo: string;
  userImage: string;
  status: "Active" | "Inactive" | "Suspended";
  lastLoginDate: string;
  createdDate: string;
  updatedDate: string;
};

const emptyForm = () => ({
  fullName: "",
  email: "",
  password: "",
  confirmPassword: "",
  mobileNo: "",
  userImage: "",
  status: "Active" as User["status"],
});

export function UsersPage() {
  const { user } = useAuth();
  const canManage = user?.role === "admin";

  const [form, setForm] = useState(emptyForm());
  const [imagePreview, setImagePreview] = useState<string>("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<User>>({});
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function hf(key: keyof typeof form, val: string) {
    setForm(prev => ({ ...prev, [key]: val }));
    setErrors(prev => ({ ...prev, [key]: "" }));
  }

  function handleImageFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const url = ev.target?.result as string;
      setImagePreview(url);
      setForm(prev => ({ ...prev, userImage: url }));
    };
    reader.readAsDataURL(file);
  }

  function validate() {
    const errs: Record<string, string> = {};
    if (!form.fullName.trim()) errs.fullName = "Full name is required";
    if (!form.email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Invalid email address";
    if (!form.password) errs.password = "Password is required";
    else if (form.password.length < 6) errs.password = "Minimum 6 characters";
    if (form.confirmPassword !== form.password) errs.confirmPassword = "Passwords do not match";
    if (!form.mobileNo.trim()) errs.mobileNo = "Mobile number is required";
    else if (!/^\d{10}$/.test(form.mobileNo.replace(/\s/g, ""))) errs.mobileNo = "Enter valid 10-digit number";
    return errs;
  }

  function handleSubmit() {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    const now = new Date().toLocaleDateString("en-IN");
    setUsers(prev => [{
      id: `usr-${Date.now()}`,
      fullName: form.fullName.trim(),
      email: form.email.trim(),
      mobileNo: form.mobileNo.trim(),
      userImage: form.userImage,
      status: form.status,
      lastLoginDate: "—",
      createdDate: now,
      updatedDate: now,
    }, ...prev]);
    setForm(emptyForm());
    setImagePreview("");
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  function startEdit(u: User) { setEditingId(u.id); setEditForm({ ...u }); }
  function saveEdit(id: string) {
    setUsers(prev => prev.map(u => u.id === id ? { ...u, ...editForm, updatedDate: new Date().toLocaleDateString("en-IN") } as User : u));
    setEditingId(null); setEditForm({});
  }
  function cancelEdit() { setEditingId(null); setEditForm({}); }
  function doDelete() {
    if (deleteConfirm) { setUsers(prev => prev.filter(u => u.id !== deleteConfirm)); setDeleteConfirm(null); }
  }
  function toggleStatus(id: string) {
    setUsers(prev => prev.map(u => u.id === id ? {
      ...u,
      status: u.status === "Active" ? "Inactive" : "Active",
      updatedDate: new Date().toLocaleDateString("en-IN"),
    } : u));
  }

  const statusColor: Record<User["status"], string> = {
    Active: "text-bg-success",
    Inactive: "text-bg-secondary",
    Suspended: "text-bg-danger",
  };

  return (
    <div className="d-grid gap-4">
      {/* ── CREATE USER FORM ── */}
      <div className="card border-0 shadow-sm mst-card">
        <div className="card-body p-4">
          <div className="d-flex align-items-center gap-2 mb-4">
            <div className="mst-icon-box"><i className="bi bi-person-plus"></i></div>
            <div>
              <h5 className="mb-0 fw-bold mst-title">Create User</h5>
              <small className="mst-subtitle">Add a new admin/staff user account</small>
            </div>
          </div>

          {saved && (
            <div className="alert alert-success d-flex align-items-center gap-2 py-2 mb-3 mst-alert">
              <i className="bi bi-check-circle-fill"></i> User created successfully!
            </div>
          )}

          <div className="row g-3">
            {/* Full Name */}
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">Full Name <span className="text-danger">*</span></label>
              <input className={`form-control mst-control ${errors.fullName ? "is-invalid" : ""}`}
                placeholder="e.g. Priya Sharma" value={form.fullName}
                onChange={e => hf("fullName", e.target.value)} />
              {errors.fullName && <div className="invalid-feedback">{errors.fullName}</div>}
            </div>

            {/* Email */}
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">Email <span className="text-danger">*</span></label>
              <input type="email" className={`form-control mst-control ${errors.email ? "is-invalid" : ""}`}
                placeholder="user@example.com" value={form.email}
                onChange={e => hf("email", e.target.value)} />
              {errors.email && <div className="invalid-feedback">{errors.email}</div>}
            </div>

            {/* Password */}
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">Password <span className="text-danger">*</span></label>
              <input type="password" className={`form-control mst-control ${errors.password ? "is-invalid" : ""}`}
                placeholder="Min. 6 characters" value={form.password}
                onChange={e => hf("password", e.target.value)} />
              {errors.password && <div className="invalid-feedback">{errors.password}</div>}
            </div>

            {/* Confirm Password */}
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">Confirm Password <span className="text-danger">*</span></label>
              <input type="password" className={`form-control mst-control ${errors.confirmPassword ? "is-invalid" : ""}`}
                placeholder="Re-enter password" value={form.confirmPassword}
                onChange={e => hf("confirmPassword", e.target.value)} />
              {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword}</div>}
            </div>

            {/* Mobile */}
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">Mobile No. <span className="text-danger">*</span></label>
              <div className="input-group">
                <span className="input-group-text mst-ig-text">+91</span>
                <input className={`form-control mst-control-right ${errors.mobileNo ? "is-invalid" : ""}`}
                  placeholder="10-digit mobile number" value={form.mobileNo}
                  onChange={e => hf("mobileNo", e.target.value.replace(/\D/g, "").slice(0, 10))} />
                {errors.mobileNo && <div className="invalid-feedback">{errors.mobileNo}</div>}
              </div>
            </div>

            {/* Status */}
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">Status</label>
              <select className="form-select mst-select" value={form.status}
                onChange={e => hf("status", e.target.value as User["status"])}>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Suspended">Suspended</option>
              </select>
            </div>

            {/* User Image */}
            <div className="col-12">
              <label className="form-label fw-semibold mst-label">User Image</label>
              <div className="usr-img-upload-row">
                {imagePreview ? (
                  <div className="usr-img-preview">
                    <img src={imagePreview} alt="Preview" />
                    <button type="button" className="usr-img-remove"
                      onClick={() => { setImagePreview(""); setForm(p => ({ ...p, userImage: "" })); }}>
                      <i className="bi bi-x"></i>
                    </button>
                  </div>
                ) : (
                  <div className="usr-img-placeholder">
                    <i className="bi bi-person-circle"></i>
                  </div>
                )}
                <div className="usr-img-controls">
                  <button type="button" className="btn mst-btn-reset"
                    onClick={() => fileInputRef.current?.click()}>
                    <i className="bi bi-upload me-2"></i>Upload Image
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*"
                    className="d-none" onChange={handleImageFile} />
                  <small className="d-block text-muted mt-1">or paste URL below</small>
                  <input className="form-control mst-control mt-1" placeholder="https://example.com/avatar.jpg"
                    value={form.userImage.startsWith("data:") ? "" : form.userImage}
                    onChange={e => { hf("userImage", e.target.value); setImagePreview(e.target.value); }} />
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex gap-2 mt-4">
            <button type="button" className="btn btn-primary px-4 mst-btn-save"
              onClick={handleSubmit}>
              <i className="bi bi-person-plus me-2"></i>Create User
            </button>
            <button type="button" className="btn mst-btn-reset"
              onClick={() => { setForm(emptyForm()); setImagePreview(""); setErrors({}); }}>
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* ── DATA TABLE ── */}
      <DataTable<User>
        title="Users"
        rows={users}
        rowKey={r => r.id}
        searchPlaceholder="Search by name, email, mobile..."
        exportFileName="users"
        columns={[
          {
            key: "fullName", label: "User", sortable: true,
            render: r => (
              <div className="d-flex align-items-center gap-2">
                <div className="usr-tbl-avatar">
                  {r.userImage
                    ? <img src={r.userImage} alt={r.fullName} />
                    : <span>{r.fullName.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()}</span>
                  }
                </div>
                <div>
                  <div className="fw-semibold" style={{ fontSize: 13 }}>{r.fullName}</div>
                  <div style={{ fontSize: 11, color: "var(--smoke)" }}>{r.email}</div>
                </div>
              </div>
            ),
          },
          { key: "mobileNo", label: "Mobile", render: r => <span style={{ fontSize: 13 }}>+91 {r.mobileNo}</span> },
          {
            key: "status", label: "Status",
            render: r => editingId === r.id
              ? <select className="form-select form-select-sm"
                  value={editForm.status ?? r.status}
                  onChange={e => setEditForm(f => ({ ...f, status: e.target.value as User["status"] }))}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Suspended">Suspended</option>
                </select>
              : <span className={`badge ${statusColor[r.status]}`}>{r.status}</span>
          },
          { key: "lastLoginDate", label: "Last Login", sortable: true },
          { key: "createdDate", label: "Created", sortable: true },
          { key: "updatedDate", label: "Updated", sortable: true },
          ...(canManage ? [{
            key: "actions" as const, label: "Actions", className: "text-end",
            render: (r: User) => editingId === r.id ? (
              <div className="pg-actions">
                <button type="button" className="btn btn-sm btn-success" onClick={() => saveEdit(r.id)}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                </button>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={cancelEdit}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            ) : (
              <div className="pg-actions">
                <button type="button" className="btn btn-sm btn-outline-primary mst-btn-status" onClick={() => startEdit(r)} title="Edit">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                </button>
                <button type="button" className="btn btn-sm btn-outline-secondary mst-btn-status" onClick={() => toggleStatus(r.id)}>
                  {r.status === "Active" ? "Disable" : "Enable"}
                </button>
                <button type="button" className="btn btn-sm btn-outline-danger mst-btn-delete" onClick={() => setDeleteConfirm(r.id)}>
                  <i className="bi bi-trash"></i>
                </button>
              </div>
            ),
          }] : []),
        ]}
      />

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="pm-del-overlay" onClick={e => { if (e.target === e.currentTarget) setDeleteConfirm(null); }}>
          <div className="pm-del-box">
            <div className="pm-del-ico">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
            </div>
            <div className="pm-del-title">Delete User?</div>
            <div className="pm-del-sub">This action cannot be undone.</div>
            <div className="pm-del-btns">
              <button className="pm-btn-ghost" onClick={() => setDeleteConfirm(null)} type="button">Cancel</button>
              <button className="pm-btn-danger" onClick={doDelete} type="button">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
